import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/services/api'
import type { GrantableCaseRole } from '@/services/api/cases'

const membersKey = (caseId: string) => ['case', caseId, 'members'] as const

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

export function useCaseMembers(caseId: string) {
  const query = useQuery({
    queryKey: membersKey(caseId),
    queryFn: () => api.cases.listMembers(caseId),
  })
  return { members: query.data ?? [], isLoading: query.isLoading, error: query.error }
}

export function useAddMember(caseId: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (input: { username: string; role: GrantableCaseRole }) =>
      api.cases.addMember(caseId, { username: input.username, role: input.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(caseId) }),
    onError: err => toast.error(message(err, 'Failed to add member')),
  })
  return { addMember: mutation.mutate, adding: mutation.isPending }
}

export function useUpdateMemberRole(caseId: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (input: { userId: string; role: GrantableCaseRole }) =>
      api.cases.updateMember(caseId, input.userId, input.role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(caseId) }),
    onError: err => toast.error(message(err, 'Failed to update member')),
  })
  return { updateRole: mutation.mutate }
}

export function useRemoveMember(caseId: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (userId: string) => api.cases.removeMember(caseId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: membersKey(caseId) }),
    onError: err => toast.error(message(err, 'Failed to remove member')),
  })
  return { removeMember: mutation.mutate }
}
